import { useState } from 'react'
import { FileText, Download, Trash2, Upload, Plus } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useEntityContent, useDownloadContent, useDeleteContent } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { ContentVersion } from '@/types'
import { UploadContentModal } from '@/components/UploadContentModal'
import { ContentDetailModal } from '@/components/ContentDetailModal'
import { toDisplayDate } from '@/lib/utils'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

interface AttachmentsTabProps {
  entityId: string
  entityType?: string
}

export function AttachmentsTab({ entityId, entityType = 'Entity' }: AttachmentsTabProps) {
  const { toast } = useToast()
  const { data, isLoading, error } = useEntityContent(entityId)
  const deleteContentMutation = useDeleteContent()
  const downloadContentMutation = useDownloadContent()

  const [selectedContentId, setSelectedContentId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const attachments = data?.data ?? []

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Are you sure you want to delete "${title}"?`)) return

    try {
      await deleteContentMutation.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'File deleted successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete file'),
        variant: 'destructive',
      })
    }
  }

  const handleDownload = async (id: string, title: string) => {
    try {
      const blob = await downloadContentMutation.mutateAsync(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = title
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      toast({
        title: 'Success',
        description: 'File downloaded successfully',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to download file'),
        variant: 'destructive',
      })
    }
  }

  const formatFileSize = (bytes: number | string) => {
    const size = typeof bytes === 'string' ? parseInt(bytes) : bytes
    if (size === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(size) / Math.log(k))
    return Math.round((size / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileTypeStyle = (type: string) => {
    // GRK-BRAND-6: file-type badges mirror RepositoryAssetThumbnail mapping.
    // Tint = fill, Prussian = text (never light text on light fills).
    const styleMap: Record<string, { backgroundColor: string; color: string }> = {
      PDF: { backgroundColor: CATEGORICAL.raspberry.tint, color: GRACKLE_COLORS.prussianBlue },
      PNG: { backgroundColor: CATEGORICAL.navy.tint, color: GRACKLE_COLORS.prussianBlue },
      JPG: { backgroundColor: CATEGORICAL.navy.tint, color: GRACKLE_COLORS.prussianBlue },
      JPEG: { backgroundColor: CATEGORICAL.navy.tint, color: GRACKLE_COLORS.prussianBlue },
      GIF: { backgroundColor: CATEGORICAL.navy.tint, color: GRACKLE_COLORS.prussianBlue },
      WORD: { backgroundColor: CATEGORICAL.steel.tint, color: GRACKLE_COLORS.prussianBlue },
      WORD_X: { backgroundColor: CATEGORICAL.steel.tint, color: GRACKLE_COLORS.prussianBlue },
      EXCEL: { backgroundColor: CATEGORICAL.evergreen.tint, color: GRACKLE_COLORS.prussianBlue },
      EXCEL_X: { backgroundColor: CATEGORICAL.evergreen.tint, color: GRACKLE_COLORS.prussianBlue },
      CSV: { backgroundColor: CATEGORICAL.evergreen.tint, color: GRACKLE_COLORS.prussianBlue },
      POWER_POINT: { backgroundColor: CATEGORICAL.clay.tint, color: GRACKLE_COLORS.prussianBlue },
      POWER_POINT_X: { backgroundColor: CATEGORICAL.clay.tint, color: GRACKLE_COLORS.prussianBlue },
      HTML: { backgroundColor: CATEGORICAL.teal.tint, color: GRACKLE_COLORS.prussianBlue },
      SNOTE: { backgroundColor: CATEGORICAL.plum.tint, color: GRACKLE_COLORS.prussianBlue },
      TEXT: { backgroundColor: CATEGORICAL.slate.tint, color: GRACKLE_COLORS.prussianBlue },
      ZIP: { backgroundColor: CATEGORICAL.slate.tint, color: GRACKLE_COLORS.prussianBlue },
    }
    return (
      styleMap[type] || {
        backgroundColor: CATEGORICAL.slate.tint,
        color: GRACKLE_COLORS.prussianBlue,
      }
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="text-sm text-muted-foreground" role="status" aria-live="polite">
            Loading attachments...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <div className="text-sm text-destructive" role="alert" aria-live="assertive">
            Error loading attachments: {explainError(error, 'Unknown error')}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (attachments.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
            <p className="text-sm text-muted-foreground mb-4">
              No attachments for this {entityType.toLowerCase()} yet.
            </p>
            <Button onClick={() => setUploadModalOpen(true)} size="sm" aria-label="Upload file">
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Upload File
            </Button>
          </CardContent>
        </Card>

        <UploadContentModal
          open={uploadModalOpen}
          onOpenChange={setUploadModalOpen}
          linkedEntityId={entityId}
        />
      </>
    )
  }

  return (
    <>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-medium">
              {attachments.length} {attachments.length === 1 ? 'file' : 'files'}
            </div>
            <Button
              onClick={() => setUploadModalOpen(true)}
              size="sm"
              variant="outline"
              aria-label="Upload file"
            >
              <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
              Upload
            </Button>
          </div>

          <div className="space-y-2">
            {attachments.map((file: ContentVersion) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText
                    className="h-5 w-5 text-muted-foreground flex-shrink-0"
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedContentId(file.id)
                          setDetailModalOpen(true)
                        }}
                        className="text-sm font-medium hover:underline text-left truncate"
                        aria-label={`View details for ${file.title}`}
                      >
                        {file.title || 'Untitled'}
                      </button>
                      <Badge
                        style={getFileTypeStyle(file.file_type)}
                        aria-label={`File type: ${file.file_type}`}
                      >
                        {file.file_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{formatFileSize(file.content_size)}</span>
                      <span>•</span>
                      <span>
                        {(
                          toDisplayDate(file.created_date) ?? new Date(file.created_date)
                        ).toLocaleDateString()}
                      </span>
                      {file.owner_name && (
                        <>
                          <span>•</span>
                          <span>{file.owner_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(file.id, file.title)}
                    disabled={downloadContentMutation.isPending}
                    aria-label={`Download ${file.title}`}
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(file.id, file.title)}
                    disabled={deleteContentMutation.isPending}
                    aria-label={`Delete ${file.title}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <UploadContentModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        linkedEntityId={entityId}
      />

      <ContentDetailModal
        contentId={selectedContentId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </>
  )
}
